/**
 * This is an extension to Leaflet allowing to display TomTom base vector map layers. It is based on the L.Class
 * implementation. The options that can be passed on creation of this object are described for the .tomTomVectorLayer()
 * function. Current vector renderer implementation is based on Mapbox GL JS: https://www.mapbox.com/mapbox-gl-js/api/
 *
 * @example
 *  var tomtomLayer = L.tomTomVectorLayer({ vector: { key: "<Your API key>" } });
 *
 * @module Map
 * @class TomTomVectorLayer
 * @extends L.Class
 */
define('display/layers/vector/vectorLayer',[
    'leaflet',
    'security',
    './styles/main',
    './configuration',
    'mapbox-gl-leaflet',
    'display/layers/mapTheme',
    './styles/night'
],
  function(L, security, mainStyles, configuration, mapboxGLLeaflet, mapTheme, nightStyles) {
      'use strict';

      var vectorMapsSecurityKey = 'services/display/vector';

      L.TomTomVectorLayer = mapboxGLLeaflet.extend({
          name: 'vector',

          onAdd: function(map) {
              mapboxGLLeaflet.prototype.onAdd.call(this, map);
              this.bringToBack();
          },

          bringToBack: function() {
              var pane = this._getPane();

              if (this._glContainer) {
                  pane.insertBefore(this._glContainer, pane.firstChild);
              }
          },

          _getTomTomStyle: function(theme) {
              var defaultStyle = mainStyles.basic;
              switch (theme.style) {
              case 'main': {
                  return mainStyles[theme.layer];
              }
              case 'night': {
                  return nightStyles[theme.layer];
              }
              default: return defaultStyle;
              }
          },

          _validateUrl: function(url) {
              var pattern = /(http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-\/]))?/;

              if (!pattern.test(url)) {
                  console.error('Bad resource URL: ' + url);
                  return false;
              }

              return true;
          },

          _getResourcesUrls: function(options) {
              return {
                  glyphs: this._validateUrl(options.glyphsUrl) ? options.glyphsUrl : '',
                  sprite: this._validateUrl(options.spriteUrl) ? options.spriteUrl : ''
              }
          },

          updateOptions: function(options) {
              var theme = mapTheme.getTheme([this.options, options]);
              this.options.style = theme.style;
              this.options.layer = theme.layer;
              this.options.renderer.style.layers = this._getTomTomStyle(theme);
              if (this._glMap) {
                  this._glMap.setStyle(this.options.renderer.style);
              }
          },

          initialize: function(options) {
              var theme, vectorOptions, key;
              options = L.extend({}, options);
              theme = mapTheme.getTheme([options, options.vector]);
              vectorOptions = L.extend({}, options.vector);
              security.setKey(vectorMapsSecurityKey, vectorOptions.key);
              key = security.getKey(vectorMapsSecurityKey);
              vectorOptions = L.extend(
                  configuration(key, this._getTomTomStyle(theme), this._getResourcesUrls(options)),
                  vectorOptions
              );
              mapboxGLLeaflet.prototype.initialize.call(this, vectorOptions);
              L.setOptions(this, vectorOptions);
              mapboxGLLeaflet.prototype.initialize.call(this, vectorOptions);
          }
      });

      /**
       * Instantiates a new instance of the TomTom base vector map layer.
       *
       * @method tomTomVectorLayer
       * @param {Object} [options] All the options that Leaflet allows, as described here:
       *     http://leafletjs.com/reference.html#tilelayer-options Additional options, which are TomTom specific, are
       *     described below.
       * @param {String} [options.style] The base map style to be used. Should be set to one of the following values:
       *     &lt;'main', 'night'&gt;. By default, the _main_ style is used.
       * @param {String} [options.layer] The base map's layer to be used. Should be set to one of the following values:
       *     &lt;'basic', 'hybrid', 'labels'&gt;. By default, the _basic_ layer is used.
       * @param {Object} [options.vector] Options specific for vector layer.
       * @param {String} [options.vector.key] This is an API key that can be obtained from http://developer.tomtom.com
       *     When the key is passed on the map's initialization, it is set globally for all the services.
       *     It can be overwritten with the tomtom.key method globally or per service
       *     with tomtom.&lt;service_name&gt;.key functions.
       *     It is also possible to pass a specific key while working with instances of the services.
       * @param {Object} [options.vector.renderer] All options that Mapbox GL JS allows, as described here:
       *    <a href="https://www.mapbox.com/mapbox-gl-js/api/#Map"
       *    target="_blank">https://www.mapbox.com/mapbox-gl-js/api/#Map</a>.
       * @param {String} [options.vector.fallback=error] Defines a way of handling missing WebGL support.
       *    Accepted values are: &lt;'error', 'raster'&gt;.
       */

      L.tomTomVectorLayer = function(options) {
          return new L.TomTomVectorLayer(options);
      };

      return L.TomTomVectorLayer;
  });

